/**
 * Renders Sabre hotel About content: Tailwind for layout chrome, CSS module for
 * renderer-generated HTML strings (sat-* classes from renderers.js).
 */
import { Component, useState, useMemo, useRef, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { parseBlob, fixMojibake } from './parse-blob';
import {
  POLICY_HEADERS,
  label,
  detectAmenities,
  normalizeSection,
  renderShortDesc,
  renderAttractions,
  renderDining,
  renderFacilities,
  renderSafetyInfo,
  renderServices,
  renderTransportationInfo,
  renderRecreation,
  renderPolicyText,
  renderPolicies,
  thingsToKnowShouldCollapseAllColumns,
} from './renderers';
// CSS Module still needed for renderer-generated inner HTML (grid layouts, venue cards, etc.)
import styles from './sabre-about-transformer.module.css';

/** Chars of raw section HTML before we add Show more (lower than CSS module default so collapse kicks in sooner). */
const SECTION_COLLAPSE_CHAR_THRESHOLD = 220;

/** Collapsed block height (px). Tailwind v1 in this repo has no max-h-32 — only custom max-h-{1,2,10,12,14,16,20}. */
const SECTION_COLLAPSED_MAX_HEIGHT_PX = 160;

function sanitize(html) {
  return DOMPurify.sanitize(html, { USE_PROFILES: { html: true, svg: true } });
}

function hideCommission(html, doShow) {
  if (doShow || !html) return html;

  return html.replace(/id=['"]hotelCommission['"]/i, "id='hotelCommission' style='display:none'");
}

function stripHtmlApproxLength(html) {
  if (!html) return 0;
  return html
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim().length;
}

function sectionNeedsCollapse(html) {
  if (!html) return false;
  return html.length > SECTION_COLLAPSE_CHAR_THRESHOLD || stripHtmlApproxLength(html) > 140;
}

/** Collapsible HTML block — uses inline max-height because TW 1.9 has no spacing-based max-h-* utilities. */
function CollapsibleSanitizedHtml({
  html,
  className,
  contentClassName,
  shouldCollapse,
  maxCollapsedPx = SECTION_COLLAPSED_MAX_HEIGHT_PX,
}) {
  const [collapsed, setCollapsed] = useState(true);
  const [overflows, setOverflows] = useState(false);
  const contentRef = useRef(null);
  const safe = sanitize(html);

  useEffect(() => {
    if (!contentRef.current || !shouldCollapse) return;

    setOverflows(contentRef.current.scrollHeight > maxCollapsedPx);
  }, [shouldCollapse, maxCollapsedPx, safe]);

  if (!safe) return null;

  if (!shouldCollapse) {
    return (
      <div
        className={[className, contentClassName].filter(Boolean).join(' ') || undefined}
        dangerouslySetInnerHTML={{ __html: safe }}
      />
    );
  }

  const showClipped = collapsed && overflows;

  return (
    <div className={className}>
      <div className="relative min-h-0">
        <div
          ref={contentRef}
          className={contentClassName || undefined}
          style={collapsed ? { maxHeight: maxCollapsedPx, overflow: 'hidden' } : undefined}
          dangerouslySetInnerHTML={{ __html: safe }}
        />
        {showClipped && (
          <div
            className="pointer-events-none absolute bottom-0 left-0 right-0 h-12"
            style={{
              background: 'linear-gradient(to bottom, rgba(255,255,255,0), #ffffff)',
            }}
          />
        )}
      </div>

      {overflows && (
        <button
          type="button"
          className="mt-3 cursor-pointer border-0 bg-transparent p-0 text-sm font-semibold text-gray-800 underline hover:text-gray-600 focus:outline-none"
          onClick={() => setCollapsed((c) => !c)}
        >
          {collapsed ? 'Show more' : 'Show less'}
        </button>
      )}
    </div>
  );
}

function Separator() {
  return <div className="border-t border-gray-200 my-10" />;
}

const SECTION_RENDERERS = {
  Attractions: renderAttractions,
  Dining: renderDining,
  Facilities: renderFacilities,
  Recreation: renderRecreation,
  SafetyInfo: renderSafetyInfo,
  Services: renderServices,
  TransportationInfo: renderTransportationInfo,
};

function SectionBlock({ section, doShowCommission }) {
  const { header, html } = section;

  const shouldCollapse = sectionNeedsCollapse(html);

  const contentHtml = useMemo(() => {
    const fallback = `<div class="sat-section-content sat-section--fallback">${normalizeSection(html)}</div>`;

    if (header === 'Policies') return renderPolicies(html, doShowCommission) || fallback;

    const renderer = SECTION_RENDERERS[header];
    if (!renderer) return fallback;

    return renderer(html) || fallback;
  }, [html, header, doShowCommission]);

  return (
    <div>
      <Separator />
      <h3 className="text-xl font-bold tracking-tight mb-4">{label(header)}</h3>
      <CollapsibleSanitizedHtml html={contentHtml} shouldCollapse={shouldCollapse} />
    </div>
  );
}

function SabreAboutTransformerInner({
  hotelDetails,
  doShowAgentCommission = true,
  headerClassName,
}) {
  const [descCollapsed, setDescCollapsed] = useState(true);
  const [descOverflows, setDescOverflows] = useState(false);
  const descRef = useRef(null);

  const hotelName = hotelDetails?.name || '';
  const isSabre = hotelDetails?.priceProvider === 'sabreLodging';

  const data = useMemo(
    () =>
      isSabre
        ? parseBlob(hotelDetails?.info || '', {
            name: hotelName,
            checkIn: hotelDetails?.policies?.checkInTime || '',
            checkOut: hotelDetails?.policies?.checkOutTime || '',
          })
        : {
            name: hotelName,
            location: '',
            checkIn: hotelDetails?.policies?.checkInTime || '',
            checkOut: hotelDetails?.policies?.checkOutTime || '',
            shortDescription: '',
            sections: [],
          },
    [hotelDetails, isSabre, hotelName],
  );

  const amenities = useMemo(() => detectAmenities(data), [data]);
  const descHtml = useMemo(
    () => renderShortDesc(data.shortDescription || ''),
    [data.shortDescription],
  );

  useEffect(() => {
    if (!descRef.current) return;

    setDescOverflows(descRef.current.scrollHeight > descRef.current.clientHeight);
  }, [descHtml]);

  const policySections = data.sections.filter((s) => POLICY_HEADERS.includes(s.header));
  const policiesSection = data.sections.find((s) => s.header === 'Policies');
  const otherSections = data.sections.filter(
    (s) => !POLICY_HEADERS.includes(s.header) && s.header !== 'Policies',
  );

  const thingsToKnowSyncCollapse = useMemo(
    () => thingsToKnowShouldCollapseAllColumns(policySections),
    [policySections],
  );

  const checkInOutChips = (data.checkIn || data.checkOut) && (
    <div className="flex flex-wrap gap-3 mt-5">
      {data.checkIn && (
        <div className="flex items-center gap-2 bg-gray-100 rounded-full px-4 py-2 text-sm">
          <span className="text-gray-500">Check-in</span>
          <span className="font-semibold">{data.checkIn}</span>
        </div>
      )}
      {data.checkOut && (
        <div className="flex items-center gap-2 bg-gray-100 rounded-full px-4 py-2 text-sm">
          <span className="text-gray-500">Check-out</span>
          <span className="font-semibold">{data.checkOut}</span>
        </div>
      )}
    </div>
  );

  if (!isSabre) {
    const rawHtml = hideCommission(fixMojibake(hotelDetails?.info || ''), doShowAgentCommission);

    return (
      <div className={styles.satRoot}>
        <h2
          className={`text-2xl font-bold tracking-tight${headerClassName ? ` ${headerClassName}` : ''}`}
        >
          {hotelName}
        </h2>
        {checkInOutChips}
        <div
          className="sm:break-normal break-words"
          dangerouslySetInnerHTML={{ __html: sanitize(rawHtml) }}
        />
      </div>
    );
  }

  return (
    <div className={`pb-12 text-gray-900 ${styles.satRoot}`}>
      {/* Hotel name heading */}
      <h2
        className={`text-2xl font-bold tracking-tight${headerClassName ? ` ${headerClassName}` : ''}`}
      >
        {hotelName}
      </h2>

      {/* Check-in / Check-out chips */}
      {checkInOutChips}

      {/* Short description — line-clamp-* is not in Tailwind 1.9; use WebKit line clamp inline */}
      {descHtml && (
        <>
          <Separator />
          <div
            ref={descRef}
            className="text-base leading-relaxed text-gray-800 break-words"
            style={
              descCollapsed
                ? {
                    display: '-webkit-box',
                    WebkitLineClamp: 5,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }
                : undefined
            }
            dangerouslySetInnerHTML={{ __html: sanitize(descHtml) }}
          />
          {descOverflows && (
            <button
              type="button"
              className="mt-3 cursor-pointer border-0 bg-transparent p-0 text-sm font-semibold text-gray-900 underline hover:text-gray-600 focus:outline-none"
              onClick={() => setDescCollapsed((c) => !c)}
            >
              {descCollapsed ? 'Show more' : 'Show less'}
            </button>
          )}
        </>
      )}

      {/* What this place offers */}
      {amenities.length > 0 && (
        <>
          <Separator />
          <h3 className="text-xl font-bold tracking-tight mb-4">What this place offers</h3>
          <div className="grid grid-cols-2 gap-0">
            {amenities.map((a) => (
              <div key={a.label} className="flex items-center gap-3 py-3 text-sm text-gray-800">
                <span dangerouslySetInnerHTML={{ __html: sanitize(a.icon) }} />
                <span>{a.label}</span>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Things to know (cancellation / deposit / guarantee) */}
      {policySections.length > 0 && (
        <div>
          <Separator />
          <h3 className="text-xl font-bold tracking-tight mb-4">Things to know</h3>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-3 sm:items-start">
            {policySections.map((s) => (
              <div key={s.header}>
                <div className="text-sm font-bold mb-2">{label(s.header)}</div>
                <CollapsibleSanitizedHtml
                  html={renderPolicyText(s.html)}
                  shouldCollapse={thingsToKnowSyncCollapse}
                  maxCollapsedPx={140}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Policies — immediately after Things to know */}
      {policiesSection && (
        <SectionBlock
          key="Policies"
          section={policiesSection}
          doShowCommission={doShowAgentCommission}
        />
      )}

      {/* Remaining content sections */}
      {otherSections.map((section, i) => (
        <SectionBlock
          key={`${section.header}-${i}`}
          section={section}
          doShowCommission={doShowAgentCommission}
        />
      ))}
    </div>
  );
}

/** Falls back to raw sanitized HTML if the structured renderer throws. */
class SabreAboutBoundary extends Component {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    const { hotelDetails, headerClassName, doShowAgentCommission } = this.props;
    const name = hotelDetails?.name || '';
    const rawHtml = hideCommission(fixMojibake(hotelDetails?.info || ''), doShowAgentCommission);

    return (
      <div className={styles.satRoot}>
        <h2
          className={`text-2xl font-bold tracking-tight${headerClassName ? ` ${headerClassName}` : ''}`}
        >
          {name}
        </h2>
        <div
          className="sm:break-normal break-words"
          dangerouslySetInnerHTML={{
            __html: sanitize(rawHtml),
          }}
        />
      </div>
    );
  }
}

export default function SabreAboutTransformer(props) {
  return (
    <SabreAboutBoundary
      hotelDetails={props.hotelDetails}
      headerClassName={props.headerClassName}
      doShowAgentCommission={props.doShowAgentCommission}
    >
      <SabreAboutTransformerInner {...props} />
    </SabreAboutBoundary>
  );
}
